import React, { useEffect, useContext, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { signOut } from "aws-amplify/auth";
import { I18n as AmplifyI18n } from "aws-amplify/utils";
import { BrowserRouter as Router, useNavigate } from "react-router";
import {
  courses as courseQuerys,
  classrooms as classroomQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import AppContainer from "@nualang/nualang-ui-components/dist/Containers/App";
import QuickCreateClassroomDialog from "@nualang/nualang-ui-components/dist/Dialogs/QuickCreateClassroomDialog/QuickCreateClassroomDialog";
import EnrollmentKeyDialog from "@nualang/nualang-ui-components/dist/Dialogs/EnrollmentKeyDialog/EnrollmentKeyDialog";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import ModeratedDialog from "@nualang/nualang-ui-components/dist/Dialogs/ModeratedDialog/ModeratedDialog";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import Common from "./Common";
import logo from "../img/logo/nualang-logo-white.svg";
import navigation from "../_nav";
import i18n from "../i18n";
import Loading from "../components/Misc/Loading";
import AcceptTerms from "../components/AcceptTerms/AcceptTerms";
import { ColorModeContext } from "../themes/ThemeProviders/ColorModeContext";
import config from "../config";
import { createClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { createClassroom as createClassroomAPI } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { createCourse as createCourseAPI } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { createCourseSection } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { createCourseMember } from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import whatsNewMarkdown from "../WhatsNew.md";
import { initChameleon, clearChameleon, identifyChameleonUser } from "../utils/helpers/chameleon";
import { getOwnUser } from "../utils/helpers/users";
import { UploadClassroomImage, UploadCourseImage } from "../utils/imageUpload";
import { getDefaultForLang } from "../utils";
import { v4 as uuidv4 } from "uuid";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";

import logger from "../logger";

import { useApp, useSubscription, useCourse, useClassroom } from "../hooks";

const MainContent = ({
  children,
  isBasicPlan,
  authenticated,
  authState,
  authData,
}) => {
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);

  const {
    joinCourseDialogOpen,
    setIsJoinCourseDialogOpen,
    newCourse,
    setNewCourse,
  } = useCourse();
  const {
    joinClassroomDialogOpen,
    setIsJoinClassroomDialogOpen,
    newClassroom,
    setNewClassroom,
    quickCreateClassroomOpen,
    setQuickCreateClassroomOpen,
  } = useClassroom();

  const handleClosePrivateClassroomDialog = () => {
    setIsJoinClassroomDialogOpen(false);
  };

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    user,
    setUser,
    loading,
    isSnackbarOpen,
    closeSnackbar,
    message,
    type,
    shareUrl,
    shareScreenOpen,
    handleCloseShare,
    isModerateDialogOpen,
    handleCloseModerateDialog,
    openSnackbar,
    startLoading,
    stopLoading,
    isCollapsed,
    setIsCollapsed,
    moderationReason,
    handleOpenModerateDialog,
    isCommunityAccessRestricted,
  } = useApp();
  const [confirm] = useConfirm(t);
  const { subscription} = useSubscription();
  // AppContainer's create-classroom gate reads isPaidUser with no isSubscriptionLoaded check, so mask loading as paid here.
  // Only mask for teacher-role accounts, and only while the subscription is genuinely still loading
  // (not when the fetch has failed), so learners and failed fetches never get the paid-only UI.
  const isTeacher = authData.attributes["custom:role"] === "teach";
  const appContainerSubscription =
    subscription.isSubscriptionLoaded || !isTeacher || !subscription.isLoading
      ? subscription
      : { ...subscription, isPaidUser: true };
  const { attributes = {} } = user;
  const [readableMarkdown, setReadableMarkdown] = useState({ md: "" });
  const [whatsNewDate, setWhatsNewDate] = useState("");

  const handleClosePrivateCourseDialog = () => setIsJoinCourseDialogOpen(false);

  // Mutations


  const createCourseMutation = courseQuerys.useCreateCourse(createCourseAPI, {
    onMutate: () => {
      startLoading();
    },

    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      const newCourse = data.Item;
      openSnackbar("course_created", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: authData.username,
          verbose: false,
        }),
      });

      await  createCourseSection(newCourse.courseId, { sectionName: t("section") });
      navigate(`/courses/${newCourse.courseId}`);
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    },
  });

    const createClassroomMutation = classroomQuerys.useCreateClassroom(
      createClassroomAPI,
      {
        onMutate: () => {
          startLoading();
        },
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: async (data) => {
          const newClassroom = data.Item;
          openSnackbar("classroom_created", "success");
          navigate(`/classrooms/${newClassroom.classroomId}`);
        },
        onError: () => {
          // createSubmittingRef.current(false);
          openSnackbar("problem_creating", "error");
        },
      },
    );

    
  const joinClassroomMutation = classroomQuerys.useJoinClassroom(
    createClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("joined_classroom", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            limit: config.limits.teachClassrooms,
            memberId: variables.username,
            verbose: false,
          }),
        });
        handleClosePrivateClassroomDialog();
        setNewClassroom("");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          variables.username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          variables.username,
          variables.username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: authData.username,
          verbose: false,
        }),
      });
      handleClosePrivateCourseDialog();
      setNewCourse("");
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const currentLanguage = i18n.language;

  // const title = newCourse ? t('join_course') : t('join_classroom');

  const joinClassroom = async (classroomId, enrollmentKey) => {
    startLoading();
    return joinClassroomMutation.mutateAsync({
      classroomId,
      username: authData.username,
      enrollmentKey,
    });
  };

  const learnLang = attributes[`custom:learnLang`];
  // custom:forLang is only ever set from the Settings form (optional there) —
  // onboarding never writes it, so it's routinely absent for new users. Fall
  // back to a locale-derived default rather than leaving the Quick Create
  // dialogs' forLang field blank.
  const forLang = attributes[`custom:forLang`] || getDefaultForLang();

  const { languages, forLanguages } = config;


  const createClassroom = (values) => {
    const {
      classroomName,
      description,
      learnLang,
      forLang,
      visibility,
      enrolmentKey,
    } = values;
    if (
      !classroomName ||
      !description ||
      !learnLang ||
      !forLang ||
      !visibility
    ) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (visibility === "private" && !enrolmentKey) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (values.enrolmentKey === "") {
      values.enrolmentKey = null;
    }

    return createClassroomMutation.mutateAsync({ values });
  };

  const handleQuickCreateClassroom = async (values) => {
      try {
        startLoading();
        const { visibility } = values;
        if (visibility !== "public" && visibility !== "private") {
          throw new Error();
        }
        if (values.picture) {
          const base64String = values.picture.split(",");
          const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
            await UploadClassroomImage(
              base64String[1] || base64String[0],
              uuidv4(),
            );
          if (moderated || warn) {
            if (currentUser.verificationStatus && currentUser.verificationStatus === "approved" || warn) {
              const confirmation = await confirm(
                `${t("warning")}`,
                `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                `${t("use_anyways")}`,
              );
              values.pictureWarnDetails = {
                user: authData.username,
                labels: moderatedLabels.join(", "),
              };
              if (confirmation !== true) {
                return;
              }
            } else {
              const moderationReason = moderatedLabels[0];
              handleOpenModerateDialog(moderationReason);
              return;
            }
          }
          if (picture) {
            values.picture = picture;
            values.pictureS3Key = pictureS3Key;
          } else {
            delete values.picture;
          }
        }
        if (values.banner) {
          const base64String = values.banner.split(",");
          const [bannerUrl, bannerS3Key] =
            await UploadClassroomImage(
              base64String[1] || base64String[0],
              uuidv4(),
            );
          if (bannerUrl) {
            values.banner = bannerUrl;
            values.bannerS3Key = bannerS3Key;
          } else {
            delete values.banner;
          }
        }
        await createClassroom(values);
        stopLoading();
      } catch (error) {
        openSnackbar("Error creating classroom", "error");
        console.error(error);
        stopLoading();
      }
    };


  const createCourse = (values) => {
    const {
      courseName,
      description,
      learnLang,
      forLang,
      visibility,
      enrolmentKey,
    } = values;

    if (!courseName || !description || !learnLang || !forLang || !visibility) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (visibility === "private" && !enrolmentKey) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (values.enrolmentKey === "") {
      values.enrolmentKey = null;
    }

    return createCourseMutation.mutateAsync({ values });
  };

  const handleQuickCreateCourse = async (values) => {
    startLoading();
    try {
      const { visibility } = values;
      if (visibility !== "public" && visibility !== "private") {
        throw new Error();
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadCourseImage(base64String[1] || base64String[0], uuidv4());
        if (moderated || warn) {
          if (currentUser.verificationStatus && currentUser.verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.pictureWarnDetails = {
              user: authData.username,
              labels: moderatedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.banner) {
        const base64String = values.banner.split(",");
        const [bannerUrl, bannerS3Key] =
          await UploadCourseImage(base64String[1] || base64String[0], uuidv4());
        if (bannerUrl) {
          values.banner = bannerUrl;
          values.bannerS3Key = bannerS3Key;
        } else {
          delete values.banner;
        }
      }
      await createCourse(values);
    } catch (error) {
      openSnackbar("Error creating course", "error");
      logger.error(error);
    } finally {
      stopLoading();
    }
  };

  const handlePrivateClassroomSubmit = async (enrollmentKey) => {
    try {
      const { allowedDomains = [] } = newClassroom;
      // if there's no allowed domains specified then allow access
      let domainMatch = !(allowedDomains.length > 0);
      for (let index = 0; index < allowedDomains.length; index += 1) {
        const domain = allowedDomains[index];
        if (authData.attributes.email.endsWith(domain)) {
          domainMatch = true;
        }
      }
      if (!domainMatch) {
        openSnackbar(
          `You cannot join this classroom without having one of the allowed email domains: ${allowedDomains.toString()}`,
          "error",
        );
      } else {
        const response = await joinClassroom(
          newClassroom.classroomId,
          enrollmentKey,
        );
        if (
          response.Item.classroomId !== null ||
          response.Item.classroomId !== undefined
        ) {
          return newClassroom.classroomId;
        }
      }
    } catch (error) {
      logger.error(error);
      if (!enrollmentKey || enrollmentKey !== newClassroom.enrolmentKey) {
        openSnackbar("Incorrect Key", "error");
      } else {
        openSnackbar(error.message, "error");
      }
    }
  };

  const joinCourse = async (courseId, enrollmentKey) => {
    startLoading();
    return joinCourseMutation.mutateAsync({
      courseId,
      username: authData.username,
      enrollmentKey,
    });
  };

  const handlePrivateCourseSubmit = async (enrollmentKey) => {
    try {
      const { allowedDomains = [] } = newCourse;
      // if there's no allowed domains specified then allow access
      let domainMatch = !(allowedDomains.length > 0);
      for (let index = 0; index < allowedDomains.length; index += 1) {
        const domain = allowedDomains[index];
        if (authData.attributes.email.endsWith(domain)) {
          domainMatch = true;
        }
      }
      if (!domainMatch) {
        openSnackbar(
          `You cannot join this course without having one of the allowed email domains: ${allowedDomains.toString()}`,
          "error",
        );
      } else {
        const response = await joinCourse(newCourse.courseId, enrollmentKey);
        if (
          response.Item.courseId !== null ||
          response.Item.courseId !== undefined
        ) {
          return newCourse.courseId;
        }
      }
    } catch (error) {
      logger.error(error);
      if (!enrollmentKey || enrollmentKey !== newClassroom.enrolmentKey) {
        openSnackbar("Incorrect Key", "error");
      } else {
        openSnackbar(error.message, "error");
      }
    }
  };

  const handleCourseDialogSubmit = async (key) => {
    const response = await handlePrivateCourseSubmit(key);
    if (response && window.location.pathname !== `/courses/${response}`) {
      if (window.location.pathname.startsWith("/classrooms")) {
        const pathname = window.location.pathname;
        const splitPath = pathname.split("/");
        const classroomId = splitPath[2];
        window.location.replace(`/classrooms/${classroomId}/${response}`);
      } else {
        window.location.replace(`/courses/${response}`);
      }
    }
  };

  const handleClassroomDialogSubmit = async (key) => {
    const response = await handlePrivateClassroomSubmit(key);
    if (response && window.location.pathname !== `/classrooms/${response}`) {
      window.location.replace(`/classrooms/${response}`);
    }
  };

  function handleChangeLanguage(language) {
    i18n.changeLanguage(language);
    AmplifyI18n.setLanguage(language);
  }

  useEffect(() => {
    const themePreference = localStorage.getItem("nualang-theme");
    if (
      !themePreference &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      // eslint-disable-next-line no-console
      colorMode.toggleColorMode();
    }
  }, [authData?.username, colorMode]); // Only run on mount

  useEffect(() => {
    setUser(authData);
    const role = authData?.attributes?.["custom:role"];

    if (role === "teach") {  
      
      initChameleon();

      // Small delay ensures script is ready
      setTimeout(() => {
        identifyChameleonUser(authData);
      }, 0);
    }
    // If user has no role selected direct them to the onboarding steps
  }, [authState, authData, setUser]); // Only re-run the effect if the authState, authData changes

  useEffect(() => {
    const regex = /: <> \(([^\)]+)\)/i;
    fetch(whatsNewMarkdown)
      .then((res) => res.text())
      .then((md) => {
        setReadableMarkdown({ md });
        setWhatsNewDate(md.match(regex)[1]);
      });
  }, []);

  const userQuery = userQuerys.useUser(
    getOwnUser,
    {
      userId: authData && authData.username,
    },
    {
      enabled: !!(authData && authData.username),
    },
  );
  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const filterExplore = (navItems) =>
    navItems.filter((navItem) => navItem.name !== "explore");

  const teachSideNav = isCommunityAccessRestricted
    ? filterExplore(navigation.teachSideNav)
    : navigation.teachSideNav;
  const teachBottomNav = isCommunityAccessRestricted
    ? filterExplore(navigation.teachBottomNav)
    : navigation.teachBottomNav;

  const handleSignOut = async () => {
    try {
      await signOut();
      clearChameleon();

      config.keysToRemove.forEach((key) => localStorage.removeItem(key));
      const username = authData?.username?.toLowerCase() || "";
      const shouldNavigate =
        !username.includes("Google") && !username.includes("Wayside");

      if (shouldNavigate) {
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <>
      {user && (
        <AppContainer
          t={t}
          logoSrc={logo}
          toggleTheme={colorMode.toggleColorMode}
          user={user.attributes}
          // handleSignOut={signOut}
          handleSignOut={handleSignOut}
          navigation={isTeacher ? teachSideNav : navigation.learnSideNav}
          bottomNav={isTeacher ? teachBottomNav : navigation.learnBottomNav}
          isSnackbarOpen={isSnackbarOpen}
          closeSnackbar={closeSnackbar}
          snackbarMessage={message}
          snackbarType={type}
          currentLanguage={currentLanguage}
          handleChangeLanguage={handleChangeLanguage}
          navToSettings={"/settings"}
          isBasicPlan={isBasicPlan}
          subscription={appContainerSubscription}
          upgradePlanURL={`${config.SITE_URL}/pricing/${
            subscription && subscription.plan && subscription.plan !== "basic"
              ? "upgrade"
              : "app"
          }`}
          authenticated={authenticated}
          whatsNewMarkdown={readableMarkdown.md}
          whatsNewDate={whatsNewDate}
          verificationStatus={
            currentUser.verificationStatus ? currentUser.verificationStatus : ""
          }
          gameAward={currentUser.gameAward ? currentUser.gameAward : ""}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isUserInternal={isUserInternal}
          isCommunityAccessRestricted={isCommunityAccessRestricted}
          handleQuickCreateClassroom={handleQuickCreateClassroom}
          handleQuickCreateCourse={handleQuickCreateCourse}
          learnLang={learnLang}
          forLang={forLang}
          fileSizeLimit={config.fileSizeLimit}
          languages={languages}
          forLanguages={forLanguages}
        >
          {children}
        </AppContainer>
      )}
      {loading ? <Loading /> : null}
      <EnrollmentKeyDialog
        t={t}
        open={joinClassroomDialogOpen}
        handleClose={handleClosePrivateClassroomDialog}
        handleSubmit={handleClassroomDialogSubmit}
        dialogTitle={t("join_classroom")}
      />
      <EnrollmentKeyDialog
        t={t}
        open={joinCourseDialogOpen}
        handleClose={handleClosePrivateCourseDialog}
        handleSubmit={handleCourseDialogSubmit}
        dialogTitle={t("join_course")}
      />
      <ShareDrawer
        t={t}
        url={shareUrl}
        open={shareScreenOpen}
        handleClose={handleCloseShare}
      />
      <ModeratedDialog
        open={isModerateDialogOpen}
        handleClose={handleCloseModerateDialog}
        t={t}
        moderationReason={moderationReason}
      />
      <QuickCreateClassroomDialog
        open={quickCreateClassroomOpen}
        handleClose={() => setQuickCreateClassroomOpen(false)}
        handleCreate={handleQuickCreateClassroom}
        t={t}
        fileSizeLimit={config.fileSizeLimit}
        verificationStatus={
          currentUser.verificationStatus ? currentUser.verificationStatus : ""
        }
        learnLang={learnLang}
        forLang={forLang}
        languages={languages}
        forLanguages={forLanguages}
        userImage={attributes.picture || null}
        openSnackbar={openSnackbar}
      />
      <AcceptTerms t={t} username={authData.username} />
    </>
  );
};

const TemplateWrapper = ({
  children,
  authState,
  authData,
  isBasicPlan,
  authenticated,
}) => {
  return (
    <Common authData={authData}>
      <Router>
        <ScrollToTop
          disableLiveChat={
            authData &&
            authData.attributes &&
            authData.attributes["custom:role"] &&
            authData.attributes["custom:role"] === "learn"
          }
        >
          <MainContent
            isBasicPlan={isBasicPlan}
            authenticated={authenticated}
            authState={authState}
            authData={authData}
          >
            {children}
          </MainContent>
        </ScrollToTop>
      </Router>
    </Common>
  );
};

TemplateWrapper.propTypes = {
  authData: PropTypes.object,
  authState: PropTypes.string,
  children: PropTypes.node,
  isBasicPlan: PropTypes.bool,
};

export default TemplateWrapper;
